<script setup lang="ts">
const { value, size = "md" } = defineProps<{
  value: string
  size?: "sm" | "md"
}>()

const copied = ref(false)
async function copy() {
  await navigator.clipboard.writeText(value)
  copied.value = true
  await new Promise((r) => setTimeout(r, 2000))
  copied.value = false
}
</script>
<template>
  <div class="my-2 min-w-0">
    <button
      type="button"
      :aria-label="$t('buttons.copy')"
      class="flex w-full min-w-0 cursor-pointer items-center rounded-lg border border-zinc-300 bg-white text-left font-mono text-sm text-zinc-800 transition-colors hover:bg-zinc-50 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:ring-offset-2 focus:ring-offset-zinc-50 focus:outline-hidden"
      :class="{
        'py-2': size == 'md',
        'py-1': size == 'sm',
      }"
      @click="copy"
    >
      <span class="min-w-0 grow truncate px-3" :title="value">
        {{ value }}
      </span>
      <div
        class="relative flex shrink-0 self-stretch"
        :class="{
          '-my-2': size == 'md',
          '-my-1': size == 'sm',
        }"
      >
        <div
          class="group relative isolate flex cursor-pointer items-center justify-center overflow-hidden rounded-r-lg border-l border-zinc-300 bg-white px-3 text-zinc-700 transition-all duration-200 hover:bg-zinc-100 focus:outline-none"
        >
          <span
            class="pointer-events-none absolute inset-0 flex translate-y-full items-center justify-center opacity-0 transition-all duration-200"
            :class="{ 'translate-y-0 opacity-100': copied }"
          >
            <SvgoPikaCheck class="size-4" />
          </span>
          <span
            class="flex translate-y-0 items-center justify-center opacity-100 transition-all duration-200"
            :class="{ '-translate-y-full opacity-0': copied }"
          >
            <SvgoPikaCopy class="size-4" />
          </span>
        </div>
        <Transition
          enter-active-class="duration-100 ease-out"
          enter-from-class="translate-y-1 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="duration-100 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-1 opacity-0"
        >
          <span
            v-if="copied"
            class="absolute right-1/2 bottom-full z-10 mb-1 translate-x-1/2 rounded-md bg-zinc-950 px-2 py-1 text-xs font-medium text-white shadow-sm"
          >
            {{ $t("copied") }}
          </span>
        </Transition>
      </div>
    </button>
    <span class="sr-only" aria-live="polite">
      {{ copied ? $t("copied") : "" }}
    </span>
  </div>
</template>
